import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Home, 
  Briefcase, 
  Upload, 
  Zap, 
  BarChart3, 
  Settings, 
  Users, 
  Menu,
  X,
  LogOut,
  User,
  Kanban,
  ChevronLeft,
  StickyNote,
  UserCheck,
  Calendar,
  Key,
  Mail
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { navigationAPI } from '../utils/api';
import brandLogo from '../assets/brand/logo.png';

const PrivateLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();

  // Persist collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // Tracker les navigations automatiquement
  useEffect(() => {
    const isNavigationTrackingEnabled =
      process.env.NODE_ENV === 'production' ||
      process.env.REACT_APP_ENABLE_NAV_TRACKING === 'true';

    const trackNavigation = async () => {
      if (user?.role === 'super_admin') return;
      if (!isNavigationTrackingEnabled) return;
      try {
        await navigationAPI.track({
          route: location.pathname,
          metadata: {
            timestamp: new Date().toISOString(),
            source: 'automatic_tracking'
          }
        });
      } catch (error) {
        // Ne pas faire échouer l'interface si le tracking échoue
      }
    };

    // Tracker après un petit délai pour éviter les navigations trop rapides
    const timer = setTimeout(() => {
      void trackNavigation();
    }, 500);
    return () => clearTimeout(timer);
  }, [location.pathname, user?.role]);

  const navigation = user?.role === 'super_admin' ? [
    // Navigation spécifique pour super admin
    { name: t('dashboard.nav.dashboard', 'Dashboard'), href: '/dashboard/super-admin', icon: Home },
    { name: t('dashboard.nav.users', 'Utilisateurs'), href: '/dashboard/super-admin/users', icon: Users },
    { name: t('dashboard.nav.env', 'Variables'), href: '/dashboard/super-admin/env', icon: Key },
    { name: t('dashboard.nav.community', 'Communauté'), href: '/dashboard/super-admin/community', icon: Mail },
  ] : [
    // Navigation standard pour admin et rh
    { name: t('dashboard.nav.dashboard', 'Accueil'), href: '/dashboard', icon: Home },
    { name: t('dashboard.nav.offers', 'Offres'), href: '/dashboard/offres', icon: Briefcase },
    { name: t('dashboard.nav.cvs', 'CV Library'), href: '/dashboard/cvs', icon: Upload },
    { name: t('dashboard.nav.applications', 'Candidatures'), href: '/dashboard/applications', icon: UserCheck },
    { name: t('dashboard.nav.kanban', 'Kanban'), href: '/dashboard/kanban', icon: Kanban },
    { name: t('dashboard.nav.notes', 'Notes'), href: '/dashboard/notes', icon: StickyNote },
    { name: t('dashboard.nav.analyzer', 'Comparateur IA'), href: '/dashboard/analyseur', icon: Zap },
    { name: t('dashboard.nav.results', 'Résultats'), href: '/dashboard/resultats', icon: BarChart3 },
    { name: t('dashboard.nav.appointments', 'Rendez-vous'), href: '/dashboard/rendez-vous', icon: Calendar },
    { name: t('dashboard.nav.settings', 'Paramètres'), href: '/dashboard/parametres', icon: Settings },
    ...(user?.role === 'admin' ? [
      { name: t('dashboard.nav.hrManagement', 'Gestion RH'), href: '/dashboard/gestion-rh', icon: Users },
    ] : []),
  ];

  const isActive = (path) => {
    // Pour le super admin, vérifier la correspondance exacte pour éviter les conflits
    if (user?.role === 'super_admin') {
      if (path === '/dashboard/super-admin') {
        return location.pathname === '/dashboard/super-admin';
      }
      return location.pathname.startsWith(path);
    }
    
    // Pour les autres rôles, garder le comportement existant
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
  };

  const roleLabel = user?.role === 'admin'
    ? t('user.role.admin', 'Administrateur')
    : t('user.role.hr', 'RH');

  const renderNavItem = (item, collapsed = false, onClick = undefined) => {
    const Icon = item.icon;
    const active = isActive(item.href);

    return (
      <div key={item.name} className="relative group">
        <Link
          to={item.href}
          onClick={onClick}
          className={`flex items-center rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 border ${
            active
              ? 'bg-indigo-50 text-indigo-700 border-indigo-100 shadow-sm'
              : 'bg-white text-gray-600 border-transparent hover:bg-gray-50 hover:text-gray-900'
          } ${collapsed ? 'justify-center' : ''}`}
          title={collapsed ? item.name : ''}
        >
          <Icon className={`h-5 w-5 ${collapsed ? '' : 'mr-3'} ${active ? 'text-indigo-600' : 'text-gray-400 group-hover:text-gray-600'}`} />
          {!collapsed && <span className="truncate">{item.name}</span>}
        </Link>

        {collapsed && (
          <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
            {item.name}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar for desktop */}
      <div className={`hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 transition-all duration-300 z-40 ${
        sidebarCollapsed ? 'lg:w-20' : 'lg:w-72'
      }`}>
        <div className="relative flex flex-col flex-grow bg-white border-r border-gray-200 overflow-visible shadow-sm">
          {/* Logo and Toggle Button */}
          <div className="flex items-center justify-between flex-shrink-0 px-4 pt-5 pb-4 border-b border-gray-200">
            <div className="flex items-center space-x-3 group">
              <div className="relative">
                <img
                  src={brandLogo}
                  alt="Candio logo"
                  className="h-8 w-8 rounded-xl object-contain shadow-sm"
                />
              </div>
              {!sidebarCollapsed && (
                <div className="flex flex-col">
                  <span className="text-lg font-black text-gray-900 tracking-tight">Candio</span>
                  <span className="text-xs text-gray-500 font-medium -mt-0.5">{t('brand.slogan', 'Moins de tri. Plus de talent.')}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Toggle Button */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="absolute top-6 right-0 translate-x-1/2 bg-white border border-gray-200 shadow-sm p-1 rounded-full text-gray-400 hover:text-gray-700 hover:shadow-md transition-all duration-200 z-50"
            title={sidebarCollapsed ? t('dashboard.sidebar.expand', 'Étendre le menu') : t('dashboard.sidebar.collapse', 'Réduire le menu')}
            aria-label={sidebarCollapsed ? t('dashboard.sidebar.expand', 'Étendre le menu') : t('dashboard.sidebar.collapse', 'Réduire le menu')}
          >
            <ChevronLeft className={`h-4 w-4 transition-transform duration-200 ${
              sidebarCollapsed ? 'rotate-180' : 'rotate-0'
            }`} />
          </button>

          {/* Navigation */}
          <nav className={`flex-1 px-3 py-4 space-y-2 ${sidebarCollapsed ? 'overflow-hidden' : 'overflow-y-auto'}`}>
            {!sidebarCollapsed && (
              <div className="px-2 pb-1">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  {t('common.navigation', 'Navigation')}
                </p>
              </div>
            )}
            {navigation.map((item) => renderNavItem(item, sidebarCollapsed))}
          </nav>

          {/* User info */}
          <div className="flex-shrink-0 border-t border-gray-200 p-3">
            {sidebarCollapsed ? (
              <div className="flex flex-col items-center gap-3">
                <div className="relative group">
                  <div className="h-9 w-9 rounded-full bg-primary-100 flex items-center justify-center">
                    <User className="h-5 w-5 text-primary-600" />
                  </div>

                  <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                    <div className="font-medium">{user?.name || user?.email}</div>
                    <div className="text-gray-300">{roleLabel}</div>
                  </div>
                </div>
                
                <button
                  onClick={handleLogout}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title={t('auth.logout', 'Déconnexion')}
                  aria-label={t('auth.logout', 'Déconnexion')}
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3 flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-9 w-9 rounded-full bg-primary-100 flex items-center justify-center">
                    <User className="h-5 w-5 text-primary-600" />
                  </div>
                </div>
                <div className="ml-3 flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">
                    {user?.name || user?.email}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {roleLabel}
                  </p>
                </div>
                <button
                  onClick={handleLogout}
                  className="ml-2 p-2 text-gray-400 hover:text-gray-700 hover:bg-white rounded-lg transition-colors"
                  title={t('auth.logout', 'Déconnexion')}
                  aria-label={t('auth.logout', 'Déconnexion')}
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <>
          <div
            className="fixed inset-0 flex z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
            <motion.div
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              className="relative flex-1 flex flex-col max-w-xs w-full bg-white border-r border-gray-200 shadow-xl"
            >
              <div className="absolute top-0 right-0 -mr-12 pt-2">
                <button
                  className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                  onClick={() => setSidebarOpen(false)}
                  aria-label={t('common.close')}
                >
                  <X className="h-6 w-6 text-white" />
                </button>
              </div>

              <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
                <div className="flex-shrink-0 flex items-center px-4 pb-4 border-b border-gray-200">
                  <img
                    src={brandLogo}
                    alt="Candio logo"
                    className="h-8 w-8 rounded-xl object-contain shadow-sm"
                  />
                  <span className="ml-2 text-lg font-semibold text-gray-900">Candio</span>
                </div>
                <nav className="mt-4 px-3 space-y-2">
                  {navigation.map((item) => renderNavItem(item, false, () => setSidebarOpen(false)))}
                </nav>
              </div>

              <div className="flex-shrink-0 border-t border-gray-200 p-3">
                <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3 flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-9 w-9 rounded-full bg-primary-100 flex items-center justify-center">
                      <User className="h-5 w-5 text-primary-600" />
                    </div>
                  </div>
                  <div className="ml-3 flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">
                      {user?.name || user?.email}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {roleLabel}
                    </p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="ml-2 p-2 text-gray-400 hover:text-gray-700 hover:bg-white rounded-lg transition-colors"
                    aria-label={t('auth.logout', 'Déconnexion')}
                  >
                    <LogOut className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}

      {/* Main content */}
      <div className={`flex flex-col flex-1 transition-all duration-300 ${
        sidebarCollapsed ? 'lg:pl-20' : 'lg:pl-72'
      }`}>
        {/* Top bar for mobile */}
        <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            aria-label={t('common.menu')}
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex items-center space-x-2">
            <img
              src={brandLogo}
              alt="Candio logo"
              className="h-6 w-6 rounded-lg object-contain shadow-sm"
            />
            <span className="text-lg font-semibold text-gray-900">Candio</span>
          </div>
          <div className="w-10" /> {/* Spacer for centering */}
        </div>


        {/* Page content */}
        <main className="flex-1 relative">
          <div className="h-full overflow-y-auto overflow-x-hidden">
            <div className="py-6">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default PrivateLayout;
